import { UploadCloudIcon } from '@/lib';
import { isEmpty } from 'lodash';
import React, { ChangeEvent, FunctionComponent, RefObject, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

interface IFileUploadInputProps {
  id?: string;
  label?: string;
  className?: string;
  error?: string;
  innerRef?: RefObject<HTMLInputElement>;
  onChange?: (files: File[] | null) => void;
}

type TInputType = IFileUploadInputProps & Omit<React.ComponentProps<'input'>, 'onChange'>;

const FileUploadInput: FunctionComponent<TInputType> = ({ id, name, label, onChange, error, innerRef, ...rest }) => {
  const { t } = useTranslation('data_source_edit');

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (onChange) {
      onChange(acceptedFiles);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: ['.gpg', '.asc', 'text/csv', 'text/plain', 'application/pgp-encrypted'],
    noClick: true,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      const files = e.target.files;

      if (files && !isEmpty(files)) {
        onChange(new Array(files[0]));
      }
    }
  };

  return (
    <div className='w-full'>
      {label && <label className='block py-2 text-left text-sm font-medium text-gray-700'>{label}</label>}
      <div
        {...getRootProps()}
        className={twMerge(
          'flex justify-center p-6 border rounded h-36',
          isDragActive ? ' border-blue-500 bg-brand-500/20' : error ? 'border-red-300 bg-red-50' : 'bg-primary'
        )}
      >
        <label
          htmlFor={id}
          className={twMerge('flex flex-col items-center gap-3 cursor-pointer bg-white rounded-md', isDragActive ? 'text-white-100' : 'text-gray-700')}
        >
          <div className={twMerge('rounded-md border p-2.5', isDragActive ? 'border-white-100 text-white-100' : error ? 'border-black-100' : '')}>
            <UploadCloudIcon className='size-5' />
          </div>
          <div className='text-center sm:px-36'>
            {isDragActive ? (
              <span>{t('file.name_and_file.file_upload_input_component.release_here')}</span>
            ) : (
              <Trans
                i18nKey={'data_source_edit:file.name_and_file.file_upload_input_component.drag_and_drop_text'}
                components={{
                  strong: <span className='font-semibold text-brand-700'></span>,
                }}
              />
            )}
          </div>
          <input id={id} ref={innerRef} name={name} type='file' className='sr-only' {...getInputProps()} {...rest} onChange={handleChange} />
        </label>
      </div>
      {error && (
        <div className='text-left '>
          <p className='mt-2 text-sm text-red-600'>{error}</p>
        </div>
      )}
    </div>
  );
};

export default FileUploadInput;
